import { CLIENT_MODULES } from '../constants/clientModules';
import { DASHBOARD_KPI_MODULES } from '../constants/dashboardKpiModules';

/** Aligns with CrmModuleSeed.RealEstateIndustryId */
export const REAL_ESTATE_INDUSTRY_ID = 1;

/** Aligns with CrmModuleSeed.ImmigrationIndustryId */
export const IMMIGRATION_INDUSTRY_ID = 2;

/** Aligns with CustomFieldSeed.EducationIndustryId */
export const EDUCATION_INDUSTRY_ID = 3;

/** Module codes tied to immigration only (IndustryId=2 in seed). */
export const IMMIGRATION_ONLY_MODULE_KEYS = new Set([
  'applicants',
  'cases',
  'countries',
  'visaTypes',
  'documentTemplates',
  'counselling',
  'appointments',
  'casePayments',
  'assignmentRules',
  'kpi_activeCases',
  'kpi_documentsPending',
  'kpi_applicationsSubmitted',
  'kpi_visaApprovals',
  'kpi_caseRevenue',
  'kpi_upcomingAppointments',
  'kpi_casePipelineOverview',
  'kpi_branchPerformance',
  'kpi_countryPerformance',
]);

/** Module codes tied to real estate only (IsCore=false, IndustryId=1 in seed). */
export const REAL_ESTATE_ONLY_MODULE_KEYS = new Set([
  'buyers',
  'propertyListing',
  'builders',
  'kpi_upcomingVisits',
  'kpi_visited',
  'kpi_deals',
  'kpi_dealsPerformance',
]);

/** Cross-industry Finance & HR add-on (IndustryId=null in seed). */
export const CROSS_INDUSTRY_ADDON_MODULE_KEYS = new Set([
  'hrDepartments',
  'hrLeaveManagement',
  'hrHolidays',
  'payroll',
  'payslips',
  'financeInvoices',
  'financePayments',
  'financeExpenses',
  'financeCommissions',
  'financeLedger',
  'financeTax',
  'financeReports',
  'financeHrPack',
  'kpi_payrollSummary',
  'kpi_expensePending',
  'kpi_revenueMtd',
  'kpi_commissionLiability',
  'kpi_feeOutstanding',
  'socialMediaAutomation',
]);

/** Reserved for education module (IndustryId=3) — separate from immigration counselling. */
export const EDUCATION_ONLY_MODULE_KEYS = new Set([
  'institutions',
  'courses',
  'students',
  'admissions',
  'educationCounselling',
  'scholarships',
  'exams',
  'studentAttendance',
  'placements',
]);

export function unwrapModuleList(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.$values)) return data.$values;
  return [];
}

export function allowedModuleCodesFromApi(modules) {
  return new Set(
    unwrapModuleList(modules)
      .map((m) => m.code ?? m.Code)
      .filter(Boolean)
  );
}

/** Union API catalog with cross-industry add-ons (social, finance pack) for Super Admin toggles. */
export function mergeIndustryAllowedModuleCodes(apiCodes, industryId) {
  const base =
    apiCodes && apiCodes.size > 0
      ? apiCodes
      : fallbackAllowedCodesForIndustry(industryId);
  return new Set([...base, ...CROSS_INDUSTRY_ADDON_MODULE_KEYS]);
}

export function filterModulesByAllowedKeys(modules, allowedCodes) {
  if (!allowedCodes || allowedCodes.size === 0) return [];
  return modules.filter((m) => allowedCodes.has(m.key));
}

/** Client-side fallback when module catalog API is unavailable. */
export function fallbackAllowedCodesForIndustry(industryId) {
  const allKeys = [...CLIENT_MODULES, ...DASHBOARD_KPI_MODULES].map((m) => m.key);
  const id = Number(industryId);
  let allowed;
  if (id === REAL_ESTATE_INDUSTRY_ID) {
    allowed = allKeys.filter((key) => !IMMIGRATION_ONLY_MODULE_KEYS.has(key) && !EDUCATION_ONLY_MODULE_KEYS.has(key));
  } else if (id === IMMIGRATION_INDUSTRY_ID) {
    allowed = allKeys.filter((key) => !REAL_ESTATE_ONLY_MODULE_KEYS.has(key) && !EDUCATION_ONLY_MODULE_KEYS.has(key));
  } else if (id === EDUCATION_INDUSTRY_ID) {
    allowed = allKeys.filter((key) => !REAL_ESTATE_ONLY_MODULE_KEYS.has(key) && !IMMIGRATION_ONLY_MODULE_KEYS.has(key));
  } else {
    allowed = allKeys.filter(
      (key) =>
        !REAL_ESTATE_ONLY_MODULE_KEYS.has(key)
        && !IMMIGRATION_ONLY_MODULE_KEYS.has(key)
        && !EDUCATION_ONLY_MODULE_KEYS.has(key)
    );
  }
  return new Set([...allowed, ...CROSS_INDUSTRY_ADDON_MODULE_KEYS]);
}

/** Force disallowed industry modules off before persisting module access JSON. */
export function sanitizeModuleFlags(flags, allowedCodes) {
  const next = { ...(flags ?? {}) };
  [...CLIENT_MODULES, ...DASHBOARD_KPI_MODULES].forEach(({ key }) => {
    if (!allowedCodes.has(key)) next[key] = false;
  });
  return next;
}
